// Beat grid from the separated DRUMS stem — the cleanest onset signal we will
// ever get for a song. One pass yields a precise tempo (fractional BPM, not
// the rounded chip value), the PHASE (where the beats actually fall in the
// window), and a downbeat vote taken from the chord timeline (chord changes
// overwhelmingly land on bar lines). Consumers: the beat-locked metronome,
// beat-snapped chord edges, and the BPM readout.
//
// Deliberately plain DSP — on isolated drums an energy-flux envelope plus
// autocorrelation is accurate and runs in ~tens of ms; no model download.
// Assumes constant tempo across the analysed window, which is also what the
// metronome assumes — rubato material fails the confidence gate and returns
// null, leaving every consumer on today's behaviour.

import type { ChordSegment } from './chordDetect';

// Bump when detection logic changes: stored grids with an older (or missing)
// version are recomputed on restore instead of trusted — the inputs (the
// stems) are still in IndexedDB, so a better algorithm reaches old songs too.
// v4 = neural tracker (Beat This) replaced the hand-tuned DSP entirely.
// v5 = metrical-level normalisation (the tracker sometimes reports the
//      eighth-note level; the owner's full-song upload read 150 on a 76 song).
export const BEAT_GRID_VERSION = 6;

export interface BeatGrid {
  v?: number; // BEAT_GRID_VERSION at computation time
  bpm: number; // median tempo, in song time (playback speed does not change it)
  period: number; // 60 / bpm, seconds
  phase: number; // song time of the first tracked beat
  downbeat: number; // song time of a beat that is beat ONE of a bar
  beatsPerBar: number;
  confidence: number; // 0..1 — how much the drum energy prefers this tracking
  // The actual tracked beat times. THIS is what the metronome and the chord
  // snapper consume — real songs drift, and a constant grid locked to the
  // average tempo beats against the music by the second chorus. The fields
  // above summarise this list for the BPM chip and older callers.
  beats: number[];
  downbeatIdx: number; // index into `beats` of a known beat one
  // Indices into `beats` that are bar "one"s, straight from the tracker.
  // Non-uniform bars (mixed meter, pickup bars) are represented faithfully;
  // consumers without it fall back to counting beatsPerBar from downbeatIdx.
  downbeatIdxs?: number[];
}

/** Map each downbeat to the index of its beat (they coincide by contract). */
function downbeatIndices(beats: number[], downbeats: number[], tol = 0.05): number[] {
  const out: number[] = [];
  let j = 0;
  for (const t of downbeats) {
    while (j < beats.length - 1 && Math.abs(beats[j + 1] - t) <= Math.abs(beats[j] - t)) j++;
    if (Math.abs(beats[j] - t) <= tol && (out.length === 0 || out[out.length - 1] !== j)) out.push(j);
  }
  return out;
}

/** Keep every 2nd beat, re-anchoring the parity at every downbeat so bar
    "one"s always survive and detection noise can't drift the phase. */
function halveBeats(beats: number[], dbIdxs: number[]): number[] {
  if (beats.length < 3) return beats;
  const keep = new Array<boolean>(beats.length).fill(false);
  const anchors = dbIdxs.length ? dbIdxs : [0];
  for (let a = 0; a < anchors.length; a++) {
    const start = anchors[a];
    const end = a + 1 < anchors.length ? anchors[a + 1] : beats.length;
    for (let i = start; i < end; i += 2) keep[i] = true;
  }
  for (let i = anchors[0] - 2; i >= 0; i -= 2) keep[i] = true;
  return beats.filter((_, i) => keep[i]);
}

/** How well `period` explains the spacing of chord changes (−1..1). */
function chordPeriodFit(changes: number[], period: number): number {
  const gaps: number[] = [];
  for (let i = 1; i < changes.length; i++) {
    const d = changes[i] - changes[i - 1];
    if (d > 0.3 && d < 12) gaps.push(d);
  }
  if (gaps.length < 3) return 0;
  let sum = 0;
  for (const d of gaps) sum += Math.cos(2 * Math.PI * (d / period));
  return sum / gaps.length;
}

/**
 * Normalise the tracker's metrical LEVEL — decide what a musician would tap.
 *
 * The model's beat list is sometimes the eighth-note level (measured: the
 * owner's full-song upload read 150 BPM where Moises says 76, while a 30 s
 * excerpt of the same song read 77). The model's own DOWNBEATS give the bar
 * away: count its beats per bar, duration-weighted so a 2-feel intro can't
 * outvote the body (the reference song opens with six bars OF TWO — a naive
 * median would "correct" a correct tracking).
 *   8/16 per bar → halve (twice for 16ths), anchored on downbeats.
 *   5–7 per bar → ambiguous (6/8? doubled 3/4?) → the chord rhythm decides.
 *   2–4 per bar → leave alone; real 2-feels exist and doubling invents beats.
 * With too few downbeats to vote, chord rhythm alone may still catch a clear
 * doubling. Never more than two halvings; never guesses without evidence.
 */
export function normalizeMetricalLevel(
  beats: number[],
  downbeats: number[],
  chordChanges?: number[],
): number[] {
  let cur = beats.slice();
  for (let iter = 0; iter < 2; iter++) {
    if (cur.length < 8) return cur;
    const dbIdxs = downbeatIndices(cur, downbeats);
    const ibis = cur.slice(1).map((t, i) => t - cur[i]).sort((x, y) => x - y);
    const med = ibis[ibis.length >> 1];
    const bpmNow = 60 / med;
    if (dbIdxs.length >= 3) {
      // duration-weighted beats-per-bar vote
      const weight = new Map<number, number>();
      let total = 0;
      for (let a = 0; a + 1 < dbIdxs.length; a++) {
        const c = dbIdxs[a + 1] - dbIdxs[a];
        const dur = cur[dbIdxs[a + 1]] - cur[dbIdxs[a]];
        if (c >= 1 && c <= 32 && dur > 0) { weight.set(c, (weight.get(c) ?? 0) + dur); total += dur; }
      }
      let cStar = 4, best = -1;
      for (const [c, w] of weight) if (w > best) { best = w; cStar = c; }

      // A: unambiguous — bars of 7+ tracker-beats mean the beat is a subdivision.
      if (cStar >= 7) { cur = halveBeats(cur, dbIdxs); continue; }

      // B: split meter vote. A 2-feel intro plus a 4-feel body reads as bars
      // of c AND 2c at once (measured on the reference: 11.0 s of 4s vs 9.3 s
      // of 8s at the doubled level — the vote alone can't call it). When both
      // levels have real weight and the current tempo is outside the range a
      // person taps, take the half that lands inside. Fires ONLY on a split —
      // a consistent fast 4/4 (punk at 170) never reaches this.
      const w1 = weight.get(cStar) ?? 0;
      const w2 = (weight.get(cStar * 2) ?? 0) + (weight.get(cStar * 2 + 1) ?? 0) + (weight.get(cStar * 2 - 1) ?? 0);
      const split = total > 0 && w1 / total >= 0.25 && w2 / total >= 0.25;
      if (split && bpmNow > 140 && bpmNow / 2 >= 60) { cur = halveBeats(cur, dbIdxs); continue; }

      // C: consistently short "bars" at a suspicious tempo while chords span
      // 2+ of them — the tracker doubled beats AND downbeats together. (A
      // true fast song with one chord per bar never fires; a fast song with
      // 2-bar harmonic rhythm is genuinely ambiguous to humans too, and the
      // halved reading is the common tap.)
      if ((cStar === 3 || cStar === 4) && bpmNow > 145 && chordChanges && chordChanges.length >= 4) {
        const bar = med * cStar;
        const gaps: number[] = [];
        for (let i = 1; i < chordChanges.length; i++) {
          const d = chordChanges[i] - chordChanges[i - 1];
          if (d > 0.3 && d < 12) gaps.push(d);
        }
        gaps.sort((x, y) => x - y);
        const medChord = gaps.length >= 3 ? gaps[gaps.length >> 1] : 0;
        if (bar < 2.0 && medChord >= bar * 1.8) { cur = halveBeats(cur, dbIdxs); continue; }
      }

      // 5-6 per bar: 6/8 vs doubled 3/4 — let the chord rhythm decide.
      if (cStar >= 5 && chordChanges && chordChanges.length >= 4) {
        if (chordPeriodFit(chordChanges, med * 2) > chordPeriodFit(chordChanges, med) + 0.1) {
          cur = halveBeats(cur, dbIdxs);
          continue;
        }
      }
      return cur;
    }
    // No usable downbeats: only a decisive chord-rhythm verdict may halve.
    if (chordChanges && chordChanges.length >= 4) {
      if (chordPeriodFit(chordChanges, med * 2) > chordPeriodFit(chordChanges, med) + 0.25) {
        cur = halveBeats(cur, []);
        continue;
      }
    }
    return cur;
  }
  return cur;
}

/** Build a BeatGrid from the neural tracker's beat/downbeat times. */
/**
 * Collapse a grid that carries TWO metrical levels at once.
 *
 * `normalizeMetricalLevel` halves a grid that is uniformly too fast; it cannot
 * help when the tracker emits half the song on the beat and half on the
 * off-beat, which is what a real failure looked like: 73 intervals of 0.40 s
 * interleaved with 70 of 0.80 s in one 91 s song. Played back that IS a click
 * that "changes between normal and double" — the grid itself alternates.
 *
 * When a big share of the gaps sit at twice the median, the median is the
 * SUBDIVISION and the real beat is the long one, so we thin: walk in order and
 * keep a beat only once a period has passed. Kept beats are original tracker
 * positions, so the grid stays phase-locked to the audio.
 */
export function unifyMetricalLevel(beats: number[], dbIdxs: number[]): { beats: number[]; downbeats: number[] } {
  const downbeats = dbIdxs.map((i) => beats[i]).filter((t) => t !== undefined);
  if (beats.length < 12) return { beats, downbeats };
  const d = beats.slice(1).map((t, i) => t - beats[i]);
  const sorted = [...d].sort((a, b) => a - b);
  const med = sorted[sorted.length >> 1];
  if (!med || med <= 0) return { beats, downbeats };
  const near = (x: number, target: number) => Math.abs(x - target) <= target * 0.15;
  // The median can land on EITHER level when the split is near 50/50, so look
  // both ways: gaps at twice the median mean the median is the subdivision,
  // gaps at half the median mean the median is already the true beat. Checking
  // only one direction misses exactly half of the broken grids — measured on a
  // real one whose median sat at 0.76 s with 76 stray 0.40 s gaps.
  // BOTH clusters must be heavily populated. A grid that is merely missing
  // beats in a sparse passage also shows double-length gaps — one real grid
  // had 21% of them and was perfectly healthy at 76.9 BPM; treating that as
  // mixed halved it to 38.5. Genuine mixing splits the gaps roughly evenly.
  const share = (target: number) => d.filter((x) => near(x, target)).length / d.length;
  const atMed = share(med), atDouble = share(med * 2), atHalf = share(med / 2);
  const MIX = 0.3;
  let period: number;
  if (atDouble >= MIX && atMed >= MIX) period = med * 2;
  else if (atHalf >= MIX && atMed >= MIX) period = med;
  else return { beats, downbeats };
  const kept: number[] = [beats[0]];
  for (const t of beats) {
    if (t - kept[kept.length - 1] >= period * 0.75) kept.push(t);
  }
  return { beats: kept, downbeats };
}

export function gridFromNeuralBeats(
  beatsIn: number[],
  downbeats: number[],
  beatsPerBar: number,
  chordChanges?: number[],
): BeatGrid | null {
  /* eslint-disable-next-line prefer-const */
  const levelled = normalizeMetricalLevel(beatsIn, downbeats, chordChanges);
  // Mixed-level grids survive normalizeMetricalLevel untouched, so unify after
  // it; downbeat TIMES are carried through and re-indexed below.
  const uni = unifyMetricalLevel(levelled, levelled.map((_, i) => i).filter((i) => downbeats.some((t) => Math.abs(levelled[i] - t) <= 0.05)));
  const beats = uni.beats;
  downbeats = uni.downbeats.length ? uni.downbeats : downbeats;
  if (beats.length < 4) return null;
  const ibis = beats.slice(1).map((t, i) => t - beats[i]).sort((a, b) => a - b);
  const medIbi = ibis[ibis.length >> 1];
  if (!medIbi || medIbi <= 0) return null;
  const downbeatIdxs: number[] = [];
  {
    let j = 0;
    for (const t of downbeats) {
      while (j < beats.length - 1 && Math.abs(beats[j + 1] - t) <= Math.abs(beats[j] - t)) j++;
      if (Math.abs(beats[j] - t) <= 0.05 && (downbeatIdxs.length === 0 || downbeatIdxs[downbeatIdxs.length - 1] !== j)) {
        downbeatIdxs.push(j);
      }
    }
  }
  const downbeatIdx = downbeatIdxs[0] ?? 0;
  return {
    v: BEAT_GRID_VERSION,
    bpm: 60 / medIbi,
    period: medIbi,
    phase: beats[0],
    downbeat: beats[downbeatIdx] ?? beats[0],
    beatsPerBar: Math.max(2, Math.min(12, Math.round(beatsPerBar || 4))),
    confidence: 1,
    beats: beats.slice(),
    downbeatIdx,
    downbeatIdxs,
  };
}

function decimate(x: Float32Array, factor: number): Float32Array {
  if (factor <= 1) return x;
  const n = Math.floor(x.length / factor);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0;
    const off = i * factor;
    for (let j = 0; j < factor; j++) s += x[off + j];
    out[i] = s / factor;
  }
  return out;
}

/** Half-wave-rectified log-energy flux — the same envelope keyBpm uses, kept
    in one place here so the phase search reads the identical signal. */
function onsetEnvelope(mono: Float32Array, sr: number) {
  const frameLen = 512;
  const hop = 128;
  const n = Math.floor((mono.length - frameLen) / hop);
  const envRate = sr / hop;
  const env = new Float64Array(Math.max(0, n));
  let prev = 0;
  let mean = 0;
  for (let f = 0; f < n; f++) {
    let s = 0;
    const off = f * hop;
    for (let i = 0; i < frameLen; i++) { const v = mono[off + i]; s += v * v; }
    const e = Math.log(1 + s);
    const d = e - prev;
    prev = e;
    const v = f === 0 ? 0 : d > 0 ? d : 0;
    env[f] = v;
    mean += v;
  }
  mean /= Math.max(1, n);
  for (let f = 0; f < n; f++) { env[f] -= mean; if (env[f] < 0) env[f] = 0; }
  return { env, envRate };
}

function envAt(env: Float64Array, pos: number): number {
  const i = Math.floor(pos);
  if (i < 0 || i + 1 >= env.length) return 0;
  const fr = pos - i;
  return env[i] * (1 - fr) + env[i + 1] * fr;
}

/**
 * Detect the beat grid of a drums-stem window.
 *
 * `bpmHint` (the mix-level estimate) only breaks octave ties; the drums decide.
 * `chordChanges` are chord boundary times used to vote for the downbeat —
 * without them the first beat is assumed to be beat one.
 */
export function detectBeatGrid(
  monoIn: Float32Array,
  sampleRate: number,
  opts: { bpmHint?: number; beatsPerBar?: number; chordChanges?: number[] },
): BeatGrid | null {
  const durationSec = monoIn.length / sampleRate;
  if (durationSec < 8) return null;

  // Silent / near-silent drums (karaoke sources, acoustic tracks): no grid.
  let rms = 0;
  const stride = Math.max(1, Math.floor(monoIn.length / 50000));
  let cnt = 0;
  for (let i = 0; i < monoIn.length; i += stride) { const v = monoIn[i]; rms += v * v; cnt++; }
  rms = Math.sqrt(rms / Math.max(1, cnt));
  if (rms < 1e-4) return null;

  const factor = Math.max(1, Math.round(sampleRate / 11025));
  const sr = sampleRate / factor;
  const mono = decimate(monoIn, factor);
  const { env, envRate } = onsetEnvelope(mono, sr);
  if (env.length < envRate * 6) return null;

  // ── tempo: autocorrelation with octave correction + hint tiebreak ──
  const minBpm = 50, maxBpm = 210;
  const minLag = Math.max(1, Math.floor((60 * envRate) / maxBpm));
  const maxLag = Math.min(env.length - 1, Math.ceil((60 * envRate) / minBpm));
  const acf = new Float64Array(maxLag + 1);
  let bestLag = minLag, bestVal = -Infinity, acfMean = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let s = 0;
    for (let f = lag; f < env.length; f++) s += env[f] * env[f - lag];
    acf[lag] = s;
    acfMean += s;
    if (s > bestVal) { bestVal = s; bestLag = lag; }
  }
  acfMean /= maxLag - minLag + 1;

  // How well a candidate period explains the CHORD rhythm: chord changes fall
  // on beats, and their spacing is near-integer beats, so cos(2π·Δ/P) peaks at
  // the true period. This replaced a blanket "prefer 90-150 BPM" multiplier
  // that quietly mis-scored slow songs — on the owner's 77 BPM reference the
  // Studio read 99, which then dragged the metronome and the snapped chord
  // edges with it. Real evidence beats a taste prior. (No bpmHint either: the
  // mix-level estimate carries that same old bias and would smuggle it back.)
  const gaps = (() => {
    const c = (opts.chordChanges ?? []).slice().sort((a, b) => a - b);
    const out: number[] = [];
    for (let i = 1; i < c.length; i++) {
      const d = c[i] - c[i - 1];
      if (d > 0.3 && d < 12) out.push(d);
    }
    return out;
  })();
  const chordFit = (periodSec: number): number => {
    if (gaps.length < 3) return 1;
    let s = 0;
    for (const d of gaps) s += Math.cos(2 * Math.PI * (d / periodSec));
    return 1 + 0.35 * (s / gaps.length); // ±35% — a tiebreak, not an override
  };
  // Candidates = every meaningful ACF peak, not octaves of the top one. On a
  // syncopated drum part the strongest lag can be a pattern length that is NOT
  // the quarter note — if the true beat is only the #3 peak, octaves of #1
  // never contain it and no amount of scoring can recover (this exact failure
  // put the reference song at 99: 77 wasn't in the candidate set at all).
  const peaks: number[] = [];
  for (let lag = minLag + 1; lag < maxLag; lag++) {
    if (acf[lag] > acf[lag - 1] && acf[lag] >= acf[lag + 1] && acf[lag] > acfMean) peaks.push(lag);
  }
  peaks.sort((a, b) => acf[b] - acf[a]);
  const candSet = new Set<number>();
  for (const p of peaks.slice(0, 8)) {
    for (const mult of [0.5, 1, 2]) {
      const lag = Math.round(p * mult);
      if (lag < minLag || lag > maxLag) continue;
      const cand = (60 * envRate) / lag;
      if (cand >= minBpm && cand <= maxBpm) candSet.add(lag);
    }
  }
  if (!candSet.size) candSet.add(bestLag);
  let chosenLag = bestLag, chosenScore = -Infinity;
  for (const lag of candSet) {
    // ACF at the candidate PLUS its double lag: the true beat also shows
    // energy at two beats, a spurious pattern-length peak usually doesn't.
    const dbl = lag * 2 <= maxLag ? acf[lag * 2] : 0;
    const score = (acf[lag] + 0.5 * dbl) * chordFit(lag / envRate);
    if (score > chosenScore) { chosenScore = score; chosenLag = lag; }
  }

  // Parabolic interpolation around the peak → fractional lag (target period).
  let lagF = chosenLag;
  if (chosenLag > minLag && chosenLag < maxLag) {
    const y0 = acf[chosenLag - 1], y1 = acf[chosenLag], y2 = acf[chosenLag + 1];
    const den = y0 - 2 * y1 + y2;
    if (Math.abs(den) > 1e-12) {
      const d = (0.5 * (y0 - y2)) / den;
      if (Math.abs(d) < 1) lagF = chosenLag + d;
    }
  }
  const P = lagF; // env frames per beat, fractional

  // ── beat tracking: constant period + slowly-varying PHASE ──
  // A rigid grid nails steady tracks but beats against human drummers; a
  // free tracker wiggles on steady tracks (measured: chord changes fell a
  // quarter-bar from its beats). This does neither: the period is global,
  // and the phase is measured per overlapping window, unwrapped, its linear
  // trend folded back into the period (a trend IS a tempo error), and beats
  // are generated from the smooth phase track. On a steady song every window
  // agrees and this IS the rigid grid; under drift the phase glides.
  const N = env.length;
  const WIN = Math.max(Math.ceil(envRate * 6), Math.ceil(P * 8)); // ≥6 s, ≥8 beats
  const HOP = Math.max(1, Math.floor(WIN / 3));
  const STEPS = 64;
  const centers: number[] = [];
  const phases: number[] = [];   // unwrapped, in env frames
  const contrasts: number[] = [];
  let prevPhase: number | null = null;
  for (let w0 = 0; w0 + WIN <= N || w0 === 0; w0 += HOP) {
    const w1 = Math.min(N, w0 + WIN);
    let bestOff = 0, bestScore = -Infinity, sum = 0;
    for (let s = 0; s < STEPS; s++) {
      const off = (s / STEPS) * P;
      let acc = 0, k = 0;
      for (let p = w0 + off; p < w1; p += P, k++) acc += envAt(env, p);
      const sc = k ? acc / k : 0;
      sum += sc;
      if (sc > bestScore) { bestScore = sc; bestOff = off; }
    }
    const mean = sum / STEPS;
    const contrast = mean > 0 ? bestScore / mean - 1 : 0;
    // The slowly-varying quantity is the ABSOLUTE phase (beat position mod P
    // in song frames) — the window-relative offset jumps by −HOP mod P every
    // window even at zero drift. Unwrap: pick the branch nearest the last.
    let ph = (((w0 + bestOff) % P) + P) % P;
    if (prevPhase != null) {
      const base = ((prevPhase % P) + P) % P;
      let d = ph - base;
      if (d > P / 2) d -= P;
      if (d < -P / 2) d += P;
      ph = prevPhase + d;
    }
    prevPhase = ph;
    centers.push((w0 + w1) / 2);
    phases.push(ph);
    contrasts.push(contrast);
    if (w1 >= N) break;
  }
  if (!centers.length) return null;

  // Weighted linear fit of phase over time: slope = systematic tempo error.
  let sw = 0, sx = 0, sy = 0, sxx = 0, sxy = 0;
  for (let i = 0; i < centers.length; i++) {
    const w = Math.max(0.05, contrasts[i]);
    sw += w; sx += w * centers[i]; sy += w * phases[i];
    sxx += w * centers[i] * centers[i]; sxy += w * centers[i] * phases[i];
  }
  const den = sw * sxx - sx * sx;
  // Slope = frames of phase gained per frame of time. A linear phase trend
  // IS a tempo error: t_k = k·P + slope·t_k + resid ⇒ period P/(1 − slope).
  let slope = Math.abs(den) > 1e-9 ? (sw * sxy - sx * sy) / den : 0;
  // One robustness round: refit with outlier windows (noisy phase estimates
  // on sparse drums) down-weighted, so a single bad window can't tilt the
  // tempo. Then the model is deliberately just linear-tempo + constant-phase:
  // per-window noise interpolated into beat positions is exactly the wobble
  // this replaced, and material the model can't carry fails the confidence
  // gate into the free-running click instead of fighting the song.
  {
    const resid0 = centers.map((c, i) => phases[i] - slope * c);
    const sortedR = [...resid0].sort((a, b) => a - b);
    const medR = sortedR[sortedR.length >> 1];
    let sw2 = 0, sx2 = 0, sy2 = 0, sxx2 = 0, sxy2 = 0;
    for (let i = 0; i < centers.length; i++) {
      const out = Math.abs(resid0[i] - medR) > 0.2 * P;
      const w = (out ? 0.1 : 1) * Math.max(0.05, contrasts[i]);
      sw2 += w; sx2 += w * centers[i]; sy2 += w * phases[i];
      sxx2 += w * centers[i] * centers[i]; sxy2 += w * centers[i] * phases[i];
    }
    const den2 = sw2 * sxx2 - sx2 * sx2;
    if (Math.abs(den2) > 1e-9) slope = (sw2 * sxy2 - sx2 * sy2) / den2;
  }
  if (!(Math.abs(slope) < 0.1)) slope = 0;
  // Constant phase for a given period: robust weighted mean of residuals.
  const constPhase = (period: number, sl: number): number => {
    const resid = centers.map((c, i) => phases[i] - sl * c);
    const sortedR = [...resid].sort((a, b) => a - b);
    const medR = sortedR[sortedR.length >> 1];
    let s2 = 0, w2 = 0;
    for (let i = 0; i < resid.length; i++) {
      if (Math.abs(resid[i] - medR) > 0.2 * period) continue;
      const w = Math.max(0.05, contrasts[i]);
      s2 += w * resid[i]; w2 += w;
    }
    return w2 > 0 ? s2 / w2 : medR;
  };
  const genBeats = (period: number, phi: number): number[] => {
    const out: number[] = [];
    for (let k = Math.max(0, Math.ceil((0 - phi) / period)); ; k++) {
      const t = k * period + phi;
      if (t >= N) break;
      if (t >= 0) out.push(t);
      if (out.length > 4000) break;
    }
    return out;
  };
  const combScore = (bf: number[]): number => {
    if (!bf.length) return 0;
    let s = 0;
    for (const f of bf) s += envAt(env, f);
    return s / bf.length;
  };

  // Two candidates, scored directly on the drums: A = pure constant grid
  // (the ACF period as-is — provably right on steady material), B = the
  // slope-corrected period (wins only when drift is real). The 30 s test
  // clip showed why this must be data-decided: the slope fit "found" 0.8 BPM
  // of drift that wasn't there and walked the phase 0.22 s off by the end.
  // A's phase: full-length comb search (the exact v1 method that put chord
  // changes on integer beats), not the noisier window-mean.
  let phiA = 0;
  {
    let bestSc = -Infinity;
    for (let s = 0; s < STEPS; s++) {
      const off = (s / STEPS) * P;
      let acc = 0, k = 0;
      for (let p = off; p < N; p += P, k++) acc += envAt(env, p);
      const sc = k ? acc / k : 0;
      if (sc > bestSc) { bestSc = sc; phiA = off; }
    }
  }
  const candA = genBeats(P, phiA);
  const candB = slope !== 0 ? genBeats(P / (1 - slope), constPhase(P / (1 - slope), slope)) : null;
  const scoreA = combScore(candA);
  const scoreB = candB ? combScore(candB) : -Infinity;
  const useB = candB !== null && scoreB > scoreA * 1.02; // B must clearly win
  const beatFramesArr = useB && candB ? candB : candA;
  const beats: number[] = beatFramesArr.map((f) => f / envRate);
  if (beats.length < 8) return null;

  const ibisArr = beats.slice(1).map((t, i) => t - beats[i]).sort((a, b) => a - b);
  const medIbi = ibisArr[Math.floor(ibisArr.length / 2)];
  if (!medIbi || medIbi <= 0) return null;
  const bpm = 60 / medIbi;

  // Confidence: contrast of drum energy on-grid vs the window average.
  let cSum = 0, cW = 0;
  for (let i = 0; i < contrasts.length; i++) { cSum += contrasts[i]; cW++; }
  const confidence = Math.max(0, Math.min(1, cW ? (cSum / cW) / 1.5 : 0));
  if (confidence < 0.2) return null;

  // ── downbeat: chord changes vote for which tracked beat is beat one ──
  const beatsPerBar = Math.max(2, Math.min(12, Math.round(opts.beatsPerBar ?? 4)));
  let downbeatIdx = 0;
  const changes = (opts.chordChanges ?? []).filter((t) => t > 0.5);
  if (changes.length >= 3) {
    const tol = Math.min(0.15, medIbi * 0.3);
    const votes = new Array<number>(beatsPerBar).fill(0);
    for (const c of changes) {
      const k = nearestBeatIdx(beats, c);
      if (k >= 0 && Math.abs(beats[k] - c) <= tol) votes[k % beatsPerBar]++;
    }
    let m = 0;
    for (let i = 1; i < beatsPerBar; i++) if (votes[i] > votes[m]) m = i;
    downbeatIdx = m;
  }

  return {
    v: BEAT_GRID_VERSION,
    bpm,
    period: medIbi,
    phase: beats[0],
    downbeat: beats[downbeatIdx] ?? beats[0],
    beatsPerBar,
    confidence,
    beats,
    downbeatIdx,
  };
}

/** Index of the beat nearest to `t`, or -1 for an empty list. */
export function nearestBeatIdx(beats: number[], t: number): number {
  if (!beats.length) return -1;
  let lo = 0, hi = beats.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (beats[mid] < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(beats[lo - 1] - t) <= Math.abs(beats[lo] - t)) return lo - 1;
  return lo;
}

/**
 * Snap chord boundaries to the TRACKED beats for DISPLAY — the stored
 * timeline stays raw. Boundaries within tolerance move to the nearest beat;
 * segments that collapse merge away; adjacent same-label segments join.
 */
export function snapChordsToGrid(segs: ChordSegment[], grid: BeatGrid): ChordSegment[] {
  if (!segs.length || !grid.beats?.length) return segs;
  const tol = Math.min(0.16, grid.period * 0.45);
  const snap = (t: number) => {
    const k = nearestBeatIdx(grid.beats, t);
    return k >= 0 && Math.abs(grid.beats[k] - t) <= tol ? grid.beats[k] : t;
  };
  const bounds: number[] = segs.map((s) => snap(s.start));
  bounds.push(snap(segs[segs.length - 1].end));
  for (let i = 1; i < bounds.length; i++) bounds[i] = Math.max(bounds[i], bounds[i - 1]);

  const out: ChordSegment[] = [];
  for (let i = 0; i < segs.length; i++) {
    const start = bounds[i], end = bounds[i + 1];
    if (end - start < 0.05) continue;
    const prev = out[out.length - 1];
    if (prev && prev.label === segs[i].label && Math.abs(prev.end - start) < 1e-6) prev.end = end;
    else out.push({ start, end, label: segs[i].label });
  }
  return out;
}
